/**
 * Hand-crafted accounting mock data for the Stainless org
 * (clowder-coffee/shein-legal). Engineers + business functions
 * mirror the live `~/.claude/technical-accounting/case-studies/
 * shein-legal/data/{team,functions}.json` seed.
 *
 * Period: most recent 1 month. ~70 commits weighted to top
 * contributors (Robert, Young-Jin, Eric Morphis, Tomer, Matt Casey,
 * CJ Quines, …) per their commits/year rate scaled down to monthly.
 * 11 commits flagged for review covering the spec's gray-area
 * patterns; 1 user-overridden CapEx → OpEx; 1 defensive
 * system_fallback.
 *
 * Cost math is per-engineer-period: each active engineer accrues
 * (annual_salary / 52 * period_weeks) for the period, spread evenly
 * across their commits in the window (per spec §Rollup math — all
 * commits weighted equally in MVP). Departed engineers excluded;
 * Rattray excluded from period costs since he doesn't write code in
 * this window (the spec's known limitation: activity-attributable
 * cost only; his comp would land in a non-activity bucket).
 *
 * Eventually replaced by a server composer reading the new
 * commit_classification_decisions table — see
 * docs/features/accounting-on-storage-redesign.md.
 */

import { IdPrefix, toPublicId } from '@lib/api-types/ids';

import type {
  AccountingProject,
  AccountingTreatment,
  AccountingView,
  ClassificationDecision,
  ClassificationRuleTrace,
  ClassificationSuggestion,
  ClassifiedCommit,
  InvestmentType,
  OpExSubtype,
} from './types';

// ---- Period --------------------------------------------------------------

// Anchored to a fixed "demo now" so screenshots are deterministic.
// 2026-06-01 12:00:00 UTC. Real composer uses live `now()`.
const NOW = 1748779200;
const DAY = 24 * 60 * 60;
const PERIOD_DAYS = 30;
const PERIOD_START = NOW - PERIOD_DAYS * DAY;
const PERIOD_END = NOW;
const PERIOD_WEEKS = PERIOD_DAYS / 7;

// ---- Engineers (active subset from shein-legal team.json) ---------------

// IDs match the case-study seed so the IDs line up if/when ingestion
// stitches in the real engineer rows.
const ENG_ROBERT = toPublicId(IdPrefix.engineer, 'robert');
const ENG_YOUNGJIN = toPublicId(IdPrefix.engineer, 'youngjin');
const ENG_MEORPHIS = toPublicId(IdPrefix.engineer, 'meorphis');
const ENG_TOMER = toPublicId(IdPrefix.engineer, 'tomer');
const ENG_MATTCASEY = toPublicId(IdPrefix.engineer, 'mattcasey');
const ENG_CJQUINES = toPublicId(IdPrefix.engineer, 'cjquines');
const ENG_RYANPAUL = toPublicId(IdPrefix.engineer, 'ryanpaul');
const ENG_SAMELBORAI = toPublicId(IdPrefix.engineer, 'samelborai');
const ENG_DACKERMAN = toPublicId(IdPrefix.engineer, 'dackerman');
const ENG_EM = toPublicId(IdPrefix.engineer, 'em');
const ENG_BKRAUSZ = toPublicId(IdPrefix.engineer, 'bkrausz');
const ENG_ALEXARENA = toPublicId(IdPrefix.engineer, 'alexarena');
const ENG_JACOBZ = toPublicId(IdPrefix.engineer, 'jacobz');
const ENG_CHARLES = toPublicId(IdPrefix.engineer, 'charlesfinkel');
const ENG_RATTRAY = toPublicId(IdPrefix.engineer, 'rattray');
const ENG_STAINLESS_BOT = toPublicId(IdPrefix.engineer, 'stainless-bot');

// Per-engineer base annual salary (USD). Spec's POC default is
// uniform $200k; nudged up for senior / founder roles to reflect
// realistic NYC API-startup comp without claiming audit accuracy.
const SALARY_USD: Record<string, number> = {
  [ENG_RATTRAY]: 250_000,
  [ENG_ROBERT]: 280_000,
  [ENG_YOUNGJIN]: 250_000,
  [ENG_MEORPHIS]: 280_000,
  [ENG_TOMER]: 220_000,
  [ENG_MATTCASEY]: 260_000,
  [ENG_CJQUINES]: 200_000,
  [ENG_RYANPAUL]: 200_000,
  [ENG_SAMELBORAI]: 200_000,
  [ENG_DACKERMAN]: 200_000,
  [ENG_EM]: 200_000,
  [ENG_BKRAUSZ]: 260_000,
  [ENG_ALEXARENA]: 190_000,
  [ENG_JACOBZ]: 190_000,
  [ENG_CHARLES]: 190_000,
  // Agent: no salary, only token cost (charged separately per commit).
  [ENG_STAINLESS_BOT]: 0,
};

// Per-commit token cost for agents (USD). Real ingest would derive
// this from a usage log; mocked here.
const AGENT_TOKEN_COST_USD: Record<string, number> = {
  [ENG_STAINLESS_BOT]: 4,
};

// ---- BusinessFunctions (from shein-legal functions.json) ----------------

const BF_CODEGEN = toPublicId(IdPrefix.business_function, 'codegen');
const BF_STUDIO = toPublicId(IdPrefix.business_function, 'studio');
const BF_API_DOCS = toPublicId(IdPrefix.business_function, 'api_docs');
const BF_SPEC_TOOLING = toPublicId(IdPrefix.business_function, 'spec_tooling');
const BF_RELEASE = toPublicId(IdPrefix.business_function, 'release_automation');

// ---- AccountingProjects -------------------------------------------------

const PROJECTS: AccountingProject[] = [
  {
    id: 'acctprj_java_sdk',
    display: 'Java SDK launch',
    business_function_id: BF_CODEGEN,
    cap_approved: true,
    cap_approved_at: NOW - 75 * DAY,
    stage: 'application_dev',
    scope_paths: ['templates/java/**', 'lib/laserjet/java/**'],
  },
  {
    id: 'acctprj_studio_diff_v2',
    display: 'Studio diff-review UX v2',
    business_function_id: BF_STUDIO,
    cap_approved: true,
    cap_approved_at: NOW - 50 * DAY,
    stage: 'application_dev',
    scope_paths: ['app/routes/diffs/**', 'ui-core/diff/**'],
  },
  {
    id: 'acctprj_openapi_31',
    display: 'OpenAPI 3.1 ingestion overhaul',
    business_function_id: BF_SPEC_TOOLING,
    cap_approved: true,
    cap_approved_at: NOW - 40 * DAY,
    stage: 'application_dev',
    scope_paths: ['spec-tooling/openapi/**', 'bin/oas-*'],
  },
  {
    id: 'acctprj_api_docs_autogen',
    display: 'API Reference Docs autogen',
    business_function_id: BF_API_DOCS,
    cap_approved: true,
    cap_approved_at: NOW - 180 * DAY,
    // Shipped 60d ago → post-implementation. Additions land OpEx.
    stage: 'post_implementation',
    scope_paths: ['api-docs/**'],
  },
  {
    id: 'acctprj_codegen_ts_v6',
    display: 'Codegen TS v6 migration (exploration)',
    business_function_id: BF_CODEGEN,
    cap_approved: false,
    cap_approved_at: null,
    // Preliminary stage — expensed even though Growth-flavored.
    stage: 'preliminary',
    scope_paths: ['templates/typescript/**'],
  },
];

// ---- Commit builders ----------------------------------------------------

let _shaSeq = 0;
function nextSha(): { sha: string; sha_short: string } {
  _shaSeq += 1;
  const sha = `${_shaSeq.toString(16).padStart(2, '0')}${'a1b2c3d4e5f67890'.repeat(3).slice(0, 38)}`;
  return { sha, sha_short: sha.slice(0, 7) };
}

interface CommitInput {
  subject: string;
  author: string;
  daysAgo: number;
  additions: number;
  deletions: number;
  files_changed: number;
  business_function_id: string;
  decision: {
    investment_type: InvestmentType;
    accounting_treatment: AccountingTreatment;
    opex_subtype?: OpExSubtype | null;
    accounting_project_id?: string | null;
    source: ClassificationDecision['source'];
    rationale?: string | null;
    decided_by_user_id?: string | null;
  };
  suggestion: {
    investment_type: Array<[InvestmentType, number]>;
    treatment: Array<[AccountingTreatment, number]>;
    rules: Array<[string, string, string]>;
  };
}

function commit(input: CommitInput): ClassifiedCommit {
  const { sha, sha_short } = nextSha();
  const committed_at = NOW - input.daysAgo * DAY;
  const candidatesInv = input.suggestion.investment_type
    .map(([type, confidence]) => ({ type, confidence }))
    .sort((a, b) => b.confidence - a.confidence);
  const candidatesTr = input.suggestion.treatment
    .map(([type, confidence]) => ({ type, confidence }))
    .sort((a, b) => b.confidence - a.confidence);

  const investmentDelta =
    candidatesInv.length >= 2 ? candidatesInv[0]!.confidence - candidatesInv[1]!.confidence : 1;
  const treatmentDelta =
    candidatesTr.length >= 2 ? candidatesTr[0]!.confidence - candidatesTr[1]!.confidence : 1;
  const flagged_for_review = investmentDelta < 0.15 || treatmentDelta < 0.15;

  const suggestion: ClassificationSuggestion = {
    investment_type_candidates: candidatesInv,
    treatment_candidates: candidatesTr,
    rule_trace: input.suggestion.rules.map(
      ([rule_id, rule_description, matched_input]): ClassificationRuleTrace => ({
        rule_id,
        rule_description,
        matched_input,
      }),
    ),
    classifier_version: 'heuristic-v0.1',
  };

  const decision: ClassificationDecision = {
    investment_type: input.decision.investment_type,
    accounting_treatment: input.decision.accounting_treatment,
    opex_subtype:
      input.decision.opex_subtype !== undefined
        ? input.decision.opex_subtype
        : input.decision.accounting_treatment === 'opex'
          ? 'rd'
          : null,
    accounting_project_id: input.decision.accounting_project_id ?? null,
    source: input.decision.source,
    decided_by_user_id: input.decision.decided_by_user_id ?? null,
    rationale: input.decision.rationale ?? null,
    decided_at: committed_at + 60 * 60,
  };

  return {
    sha,
    sha_short,
    subject: input.subject,
    url: `https://github.com/clowder-coffee/shein-legal/commit/${sha}`,
    author_engineer_id: input.author,
    committed_at,
    additions: input.additions,
    deletions: input.deletions,
    files_changed: input.files_changed,
    repo: 'shein-legal',
    business_function_id: input.business_function_id,
    cost_usd: 0, // recomputed in composer; per-engineer-period / commits
    suggestion,
    decision,
    flagged_for_review,
  };
}

// Helpers — clear-cut and gray-area shapes.
function clear(
  type: InvestmentType,
  treatment: AccountingTreatment,
): CommitInput['suggestion'] {
  const otherInv: InvestmentType[] = (['ktlo', 'enablement', 'growth'] as const).filter(
    (t) => t !== type,
  );
  const otherTr: AccountingTreatment = treatment === 'opex' ? 'capex' : 'opex';
  return {
    investment_type: [
      [type, 0.92],
      [otherInv[0]!, 0.05],
      [otherInv[1]!, 0.03],
    ],
    treatment: [
      [treatment, 0.91],
      [otherTr, 0.09],
    ],
    rules: [],
  };
}

function gray(
  topInv: InvestmentType,
  closeInv: InvestmentType,
  treatment: AccountingTreatment,
): CommitInput['suggestion'] {
  const otherInv = (['ktlo', 'enablement', 'growth'] as const).find(
    (t) => t !== topInv && t !== closeInv,
  )!;
  return {
    investment_type: [
      [topInv, 0.46],
      [closeInv, 0.41],
      [otherInv, 0.13],
    ],
    treatment: [
      [treatment, 0.68],
      [treatment === 'opex' ? 'capex' : 'opex', 0.32],
    ],
    rules: [],
  };
}

function pin(
  s: CommitInput['suggestion'],
  ...rules: Array<[string, string, string]>
): CommitInput['suggestion'] {
  return { ...s, rules };
}

// ---- The fixtures -------------------------------------------------------

// Distribution targets per engineer over 30 days (scaled from commits1y):
//   robert (1119/y) → 12  | youngjin (1088/y) → 12 | meorphis (827/y) → 9
//   tomer (397/y)   →  6  | mattcasey (368/y) →  5 | cjquines (229/y) →  4
//   ryanpaul (253)  →  4  | samelborai (210)  →  3 | dackerman (171)  →  3
//   em (65)         →  2  | bkrausz (47)      →  2 | alexarena (14)   →  1
//   jacobz (14)     →  1  | charles (5)       →  1 | rattray (7)      →  0
//   stainless-bot   →  6  (dep bumps / version-bot PRs)
// Total: ~71

const COMMITS: ClassifiedCommit[] = [
  // ===== Java SDK launch — Growth + CapEx (acctprj_java_sdk) =====
  commit({
    subject: 'feat(java): kotlinx-serialization adapter for sealed-class union types',
    author: ENG_TOMER,
    daysAgo: 28,
    additions: 412,
    deletions: 18,
    files_changed: 9,
    business_function_id: BF_CODEGEN,
    decision: { investment_type: 'growth', accounting_treatment: 'capex', accounting_project_id: 'acctprj_java_sdk', source: 'system_auto' },
    suggestion: pin(
      clear('growth', 'capex'),
      ['path.template_new', 'New template files under approved project scope', 'templates/java/serialization/**'],
      ['project.cap_approved', 'Linked project approved + application_dev', 'acctprj_java_sdk'],
    ),
  }),
  commit({
    subject: 'feat(java): paginated list-iterator with async support',
    author: ENG_TOMER,
    daysAgo: 24,
    additions: 287,
    deletions: 4,
    files_changed: 6,
    business_function_id: BF_CODEGEN,
    decision: { investment_type: 'growth', accounting_treatment: 'capex', accounting_project_id: 'acctprj_java_sdk', source: 'system_auto' },
    suggestion: pin(clear('growth', 'capex'), ['project.cap_approved', 'Linked project approved + application_dev', 'acctprj_java_sdk']),
  }),
  commit({
    subject: 'feat(java): retries with full jitter + Retry-After honoring',
    author: ENG_ROBERT,
    daysAgo: 21,
    additions: 198,
    deletions: 11,
    files_changed: 4,
    business_function_id: BF_CODEGEN,
    decision: { investment_type: 'growth', accounting_treatment: 'capex', accounting_project_id: 'acctprj_java_sdk', source: 'system_auto' },
    suggestion: pin(clear('growth', 'capex'), ['project.cap_approved', 'Linked project approved + application_dev', 'acctprj_java_sdk']),
  }),
  commit({
    subject: 'feat(java): bidirectional binding for nested optional unions',
    author: ENG_TOMER,
    daysAgo: 17,
    additions: 356,
    deletions: 22,
    files_changed: 7,
    business_function_id: BF_CODEGEN,
    decision: { investment_type: 'growth', accounting_treatment: 'capex', accounting_project_id: 'acctprj_java_sdk', source: 'system_auto' },
    suggestion: pin(clear('growth', 'capex'), ['project.cap_approved', 'Linked project approved + application_dev', 'acctprj_java_sdk']),
  }),
  commit({
    subject: 'feat(java): structured streaming responses for chat/completions',
    author: ENG_ROBERT,
    daysAgo: 13,
    additions: 489,
    deletions: 14,
    files_changed: 8,
    business_function_id: BF_CODEGEN,
    decision: { investment_type: 'growth', accounting_treatment: 'capex', accounting_project_id: 'acctprj_java_sdk', source: 'system_auto' },
    suggestion: pin(clear('growth', 'capex'), ['project.cap_approved', 'Linked project approved + application_dev', 'acctprj_java_sdk']),
  }),
  commit({
    subject: 'fix(java): bad validate calls on nullable nested records',
    author: ENG_TOMER,
    daysAgo: 10,
    additions: 28,
    deletions: 4,
    files_changed: 2,
    business_function_id: BF_CODEGEN,
    decision: { investment_type: 'growth', accounting_treatment: 'capex', accounting_project_id: 'acctprj_java_sdk', source: 'system_auto' },
    suggestion: pin(
      gray('growth', 'ktlo', 'capex'),
      ['title.fix_prefix', 'Title begins with fix( — KTLO signal', '^fix\\('],
      ['project.cap_approved', 'Touches an approved project scope in application_dev', 'acctprj_java_sdk'],
      ['classifier.ambiguous', 'Defect resolution within application-dev project can be CapEx', 'Δ confidence inv: 0.05'],
    ),
  }),
  commit({
    subject: 'feat(java): Maven Central publishing workflow',
    author: ENG_TOMER,
    daysAgo: 5,
    additions: 134,
    deletions: 8,
    files_changed: 3,
    business_function_id: BF_CODEGEN,
    decision: { investment_type: 'growth', accounting_treatment: 'capex', accounting_project_id: 'acctprj_java_sdk', source: 'system_auto' },
    suggestion: pin(clear('growth', 'capex'), ['project.cap_approved', 'Linked project approved + application_dev', 'acctprj_java_sdk']),
  }),

  // ===== Studio diff-review v2 — Growth + CapEx (acctprj_studio_diff_v2) =====
  commit({
    subject: 'feat(studio): inline-diff component with rename detection',
    author: ENG_YOUNGJIN,
    daysAgo: 27,
    additions: 542,
    deletions: 18,
    files_changed: 9,
    business_function_id: BF_STUDIO,
    decision: { investment_type: 'growth', accounting_treatment: 'capex', accounting_project_id: 'acctprj_studio_diff_v2', source: 'system_auto' },
    suggestion: pin(
      clear('growth', 'capex'),
      ['path.app_routes', 'New files under approved project scope', 'app/routes/diffs/inline/**'],
      ['project.cap_approved', 'Linked project approved + application_dev', 'acctprj_studio_diff_v2'],
    ),
  }),
  commit({
    subject: 'feat(studio): side-by-side diff with synchronized scroll',
    author: ENG_YOUNGJIN,
    daysAgo: 22,
    additions: 478,
    deletions: 32,
    files_changed: 7,
    business_function_id: BF_STUDIO,
    decision: { investment_type: 'growth', accounting_treatment: 'capex', accounting_project_id: 'acctprj_studio_diff_v2', source: 'system_auto' },
    suggestion: pin(clear('growth', 'capex'), ['project.cap_approved', 'Linked project approved + application_dev', 'acctprj_studio_diff_v2']),
  }),
  commit({
    subject: 'feat(studio): per-language diff filter dropdown',
    author: ENG_SAMELBORAI,
    daysAgo: 19,
    additions: 234,
    deletions: 12,
    files_changed: 5,
    business_function_id: BF_STUDIO,
    decision: { investment_type: 'growth', accounting_treatment: 'capex', accounting_project_id: 'acctprj_studio_diff_v2', source: 'system_auto' },
    suggestion: pin(clear('growth', 'capex'), ['project.cap_approved', 'Linked project approved + application_dev', 'acctprj_studio_diff_v2']),
  }),
  commit({
    subject: 'feat(studio): reviewer-comments thread on diff hunks',
    author: ENG_YOUNGJIN,
    daysAgo: 14,
    additions: 612,
    deletions: 8,
    files_changed: 11,
    business_function_id: BF_STUDIO,
    decision: { investment_type: 'growth', accounting_treatment: 'capex', accounting_project_id: 'acctprj_studio_diff_v2', source: 'system_auto' },
    suggestion: pin(clear('growth', 'capex'), ['project.cap_approved', 'Linked project approved + application_dev', 'acctprj_studio_diff_v2']),
  }),
  commit({
    subject: 'feat(studio): change breadcrumbs to StyledLink',
    author: ENG_YOUNGJIN,
    daysAgo: 11,
    additions: 78,
    deletions: 92,
    files_changed: 14,
    business_function_id: BF_STUDIO,
    decision: { investment_type: 'enablement', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('enablement', 'opex'),
      ['path.ui_core', 'Internal component refactor — not net-new functionality', 'ui-core/breadcrumbs/**'],
    ),
  }),
  commit({
    subject: 'feat(studio): diff-state persistence across review sessions',
    author: ENG_SAMELBORAI,
    daysAgo: 7,
    additions: 198,
    deletions: 6,
    files_changed: 4,
    business_function_id: BF_STUDIO,
    decision: { investment_type: 'growth', accounting_treatment: 'capex', accounting_project_id: 'acctprj_studio_diff_v2', source: 'system_auto' },
    suggestion: pin(clear('growth', 'capex'), ['project.cap_approved', 'Linked project approved + application_dev', 'acctprj_studio_diff_v2']),
  }),

  // ===== OpenAPI 3.1 ingestion — Growth + CapEx (acctprj_openapi_31) =====
  commit({
    subject: 'feat(spec): OpenAPI 3.1 schema-composition walker (allOf/oneOf/anyOf)',
    author: ENG_MEORPHIS,
    daysAgo: 26,
    additions: 487,
    deletions: 32,
    files_changed: 8,
    business_function_id: BF_SPEC_TOOLING,
    decision: { investment_type: 'growth', accounting_treatment: 'capex', accounting_project_id: 'acctprj_openapi_31', source: 'system_auto' },
    suggestion: pin(
      clear('growth', 'capex'),
      ['path.spec_tooling', 'New files under approved project scope', 'spec-tooling/openapi/31/**'],
      ['project.cap_approved', 'Linked project approved + application_dev', 'acctprj_openapi_31'],
    ),
  }),
  commit({
    subject: 'feat(spec): JSON-Schema 2020-12 unevaluatedProperties support',
    author: ENG_MEORPHIS,
    daysAgo: 20,
    additions: 312,
    deletions: 18,
    files_changed: 5,
    business_function_id: BF_SPEC_TOOLING,
    decision: { investment_type: 'growth', accounting_treatment: 'capex', accounting_project_id: 'acctprj_openapi_31', source: 'system_auto' },
    suggestion: pin(clear('growth', 'capex'), ['project.cap_approved', 'Linked project approved + application_dev', 'acctprj_openapi_31']),
  }),
  commit({
    subject: 'feat(spec): webhook-spec discovery + binding emission',
    author: ENG_MEORPHIS,
    daysAgo: 15,
    additions: 256,
    deletions: 4,
    files_changed: 4,
    business_function_id: BF_SPEC_TOOLING,
    decision: { investment_type: 'growth', accounting_treatment: 'capex', accounting_project_id: 'acctprj_openapi_31', source: 'system_auto' },
    suggestion: pin(clear('growth', 'capex'), ['project.cap_approved', 'Linked project approved + application_dev', 'acctprj_openapi_31']),
  }),
  commit({
    subject: 'feat(spec): config-language directives for 3.1 polymorphism',
    author: ENG_DACKERMAN,
    daysAgo: 9,
    additions: 178,
    deletions: 12,
    files_changed: 4,
    business_function_id: BF_SPEC_TOOLING,
    decision: { investment_type: 'growth', accounting_treatment: 'capex', accounting_project_id: 'acctprj_openapi_31', source: 'system_auto' },
    suggestion: pin(clear('growth', 'capex'), ['project.cap_approved', 'Linked project approved + application_dev', 'acctprj_openapi_31']),
  }),
  commit({
    subject: 'feat(spec): avoid yaml patch changing unrelated line wrapping',
    author: ENG_BKRAUSZ,
    daysAgo: 4,
    additions: 167,
    deletions: 23,
    files_changed: 3,
    business_function_id: BF_SPEC_TOOLING,
    decision: { investment_type: 'enablement', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      gray('enablement', 'ktlo', 'opex'),
      ['title.refactor', 'Quality-of-life improvement to YAML edit tooling', 'subject mentions "yaml patch"'],
    ),
  }),

  // ===== Codegen TS v6 migration — Growth + OpEx (preliminary stage) =====
  commit({
    subject: 'spike(ts): explore Effect for typed-error sum-type narrowing',
    author: ENG_ROBERT,
    daysAgo: 25,
    additions: 198,
    deletions: 76,
    files_changed: 4,
    business_function_id: BF_CODEGEN,
    decision: { investment_type: 'growth', accounting_treatment: 'opex', opex_subtype: 'rd', accounting_project_id: 'acctprj_codegen_ts_v6', source: 'system_auto' },
    suggestion: pin(
      clear('growth', 'opex'),
      ['title.spike_prefix', 'Title begins with spike — preliminary exploration', '^spike\\('],
      ['project.stage_preliminary', 'Linked project in preliminary stage — expensed even if Growth', 'acctprj_codegen_ts_v6'],
    ),
  }),
  commit({
    subject: 'spike(ts): prototype Hono-style routes typed-handler generation',
    author: ENG_ROBERT,
    daysAgo: 18,
    additions: 156,
    deletions: 12,
    files_changed: 3,
    business_function_id: BF_CODEGEN,
    decision: { investment_type: 'growth', accounting_treatment: 'opex', opex_subtype: 'rd', accounting_project_id: 'acctprj_codegen_ts_v6', source: 'system_auto' },
    suggestion: pin(
      clear('growth', 'opex'),
      ['title.spike_prefix', 'Title begins with spike — preliminary exploration', '^spike\\('],
      ['project.stage_preliminary', 'Linked project in preliminary stage — expensed', 'acctprj_codegen_ts_v6'],
    ),
  }),
  commit({
    subject: 'rfc: codegen TS v6 migration plan',
    author: ENG_ROBERT,
    daysAgo: 12,
    additions: 0,
    deletions: 0,
    files_changed: 1,
    business_function_id: BF_CODEGEN,
    decision: { investment_type: 'growth', accounting_treatment: 'opex', opex_subtype: 'rd', accounting_project_id: 'acctprj_codegen_ts_v6', source: 'system_auto' },
    suggestion: pin(
      clear('growth', 'opex'),
      ['path.rfc_only', 'RFC document — preliminary design', 'docs/rfcs/codegen-ts-v6.md'],
      ['project.stage_preliminary', 'Linked project in preliminary stage', 'acctprj_codegen_ts_v6'],
    ),
  }),

  // ===== API Reference Docs autogen — post-implementation (additions = OpEx) =====
  commit({
    subject: 'feat(api-docs): enum-member descriptions surfaced in side rail',
    author: ENG_RYANPAUL,
    daysAgo: 23,
    additions: 178,
    deletions: 8,
    files_changed: 4,
    business_function_id: BF_API_DOCS,
    decision: { investment_type: 'growth', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('growth', 'opex'),
      ['project.stage_post_implementation', 'Linked project in post-implementation — new functionality additions are OpEx', 'acctprj_api_docs_autogen'],
    ),
  }),
  commit({
    subject: 'feat(api-docs): code-example tabs for TS / Python / Go / Java',
    author: ENG_RYANPAUL,
    daysAgo: 16,
    additions: 234,
    deletions: 18,
    files_changed: 5,
    business_function_id: BF_API_DOCS,
    decision: { investment_type: 'growth', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('growth', 'opex'),
      ['project.stage_post_implementation', 'Linked project in post-implementation — new functionality additions are OpEx', 'acctprj_api_docs_autogen'],
    ),
  }),
  commit({
    subject: 'fix(api-docs): broken anchor on deeply-nested array params',
    author: ENG_RYANPAUL,
    daysAgo: 6,
    additions: 12,
    deletions: 4,
    files_changed: 1,
    business_function_id: BF_API_DOCS,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('ktlo', 'opex'),
      ['title.fix_prefix', 'Title begins with fix( — KTLO signal', '^fix\\('],
    ),
  }),
  // Gray-area: bug fix on the shipped docs site. Could be considered
  // post-implementation maintenance (clear KTLO) but the engineer might
  // argue it's a directly-attributable enhancement.
  commit({
    subject: 'fix(api-docs): copy-to-clipboard button missing on Safari',
    author: ENG_RYANPAUL,
    daysAgo: 2,
    additions: 22,
    deletions: 4,
    files_changed: 1,
    business_function_id: BF_API_DOCS,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      gray('ktlo', 'growth', 'opex'),
      ['title.fix_prefix', 'Title begins with fix( — KTLO signal', '^fix\\('],
      ['project.stage_post_implementation', 'In post-implementation; fix may relate to recent enhancement', 'acctprj_api_docs_autogen'],
    ),
  }),

  // ===== Robert: Anthropic SDK update (Growth, customer-facing addition) =====
  commit({
    subject: 'Anthropic: API updates — files/citations/web-search tools',
    author: ENG_ROBERT,
    daysAgo: 8,
    additions: 1519,
    deletions: 84,
    files_changed: 24,
    business_function_id: BF_CODEGEN,
    decision: { investment_type: 'growth', accounting_treatment: 'opex', opex_subtype: 'cogs', source: 'system_auto' },
    suggestion: pin(
      clear('growth', 'opex'),
      ['path.template_change', 'Customer-specific template updates — directly delivers customer value', 'templates/anthropic/**'],
      ['classifier.customer_specific', 'No CapEx project link — customer-specific SDK updates flow as OpEx COGS', '—'],
    ),
  }),

  // ===== KTLO: dep bumps, fixes, version maintenance =====
  commit({
    subject: 'chore(deps): bump esbuild to 0.24.2 (CVE-2024-50612)',
    author: ENG_STAINLESS_BOT,
    daysAgo: 29,
    additions: 4,
    deletions: 4,
    files_changed: 2,
    business_function_id: BF_RELEASE,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('ktlo', 'opex'),
      ['title.chore_deps', 'Title matches ^chore\\(deps\\) — dependency maintenance', '^chore\\(deps\\)'],
      ['path.lockfile_only', 'Only package.json + pnpm-lock.yaml changed', 'package.json + pnpm-lock.yaml'],
    ),
  }),
  commit({
    subject: 'chore(deps): bump @types/node to v22 + ESLint v9',
    author: ENG_STAINLESS_BOT,
    daysAgo: 26,
    additions: 67,
    deletions: 38,
    files_changed: 4,
    business_function_id: BF_RELEASE,
    decision: { investment_type: 'enablement', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      gray('enablement', 'ktlo', 'opex'),
      ['title.chore_deps', 'Title matches ^chore\\(deps\\)', '^chore\\(deps\\)'],
      ['path.eslint_config', 'ESLint v9 migration is internal tooling', 'eslint.config.js'],
    ),
  }),
  commit({
    subject: 'chore(deps): bump @cloudflare/workers-types',
    author: ENG_STAINLESS_BOT,
    daysAgo: 22,
    additions: 2,
    deletions: 2,
    files_changed: 2,
    business_function_id: BF_RELEASE,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'cogs', source: 'user_pin', decided_by_user_id: ENG_MATTCASEY, rationale: 'Workers-types affects our prod runtime — COGS, not R&D.' },
    suggestion: pin(
      gray('ktlo', 'enablement', 'opex'),
      ['title.chore_deps', 'Dependency maintenance', '^chore\\(deps\\)'],
      ['classifier.ambiguous', 'Workers-types could be dev-only (Enablement) or runtime (KTLO+COGS)', 'Δ confidence inv: 0.08'],
    ),
  }),
  commit({
    subject: 'fix(ts): don\'t set required client opts twice',
    author: ENG_TOMER,
    daysAgo: 28,
    additions: 10,
    deletions: 6,
    files_changed: 2,
    business_function_id: BF_CODEGEN,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('ktlo', 'opex'),
      ['title.fix_prefix', 'Title begins with fix( — KTLO signal', '^fix\\('],
    ),
  }),
  commit({
    subject: 'fix(ts): return don\'t throw error in poll-completion handler',
    author: ENG_EM,
    daysAgo: 24,
    additions: 18,
    deletions: 14,
    files_changed: 2,
    business_function_id: BF_CODEGEN,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('ktlo', 'opex'),
      ['title.fix_prefix', 'Title begins with fix( — KTLO signal', '^fix\\('],
    ),
  }),
  commit({
    subject: 'fix: pass oasRef to Method_RequestBodyNotSupported diagnostic',
    author: ENG_TOMER,
    daysAgo: 18,
    additions: 4,
    deletions: 2,
    files_changed: 1,
    business_function_id: BF_SPEC_TOOLING,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(clear('ktlo', 'opex'), ['title.fix_prefix', 'Title begins with fix( — KTLO signal', '^fix\\(']),
  }),
  commit({
    subject: 'fix(rb): prune unused extern module emission',
    author: ENG_EM,
    daysAgo: 15,
    additions: 2,
    deletions: 12,
    files_changed: 1,
    business_function_id: BF_CODEGEN,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(clear('ktlo', 'opex'), ['title.fix_prefix', 'Title begins with fix( — KTLO signal', '^fix\\(']),
  }),
  commit({
    subject: 'fix(go): nil-deref on absent server-sent-events Content-Type',
    author: ENG_ROBERT,
    daysAgo: 12,
    additions: 14,
    deletions: 4,
    files_changed: 2,
    business_function_id: BF_CODEGEN,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(clear('ktlo', 'opex'), ['title.fix_prefix', 'Title begins with fix( — KTLO signal', '^fix\\(']),
  }),
  commit({
    subject: 'fix(release): npm provenance attestation missing on prerelease',
    author: ENG_SAMELBORAI,
    daysAgo: 10,
    additions: 18,
    deletions: 4,
    files_changed: 1,
    business_function_id: BF_RELEASE,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'cogs', source: 'system_auto' },
    suggestion: pin(
      clear('ktlo', 'opex'),
      ['title.fix_prefix', 'Title begins with fix( — KTLO signal', '^fix\\('],
      ['path.release_pipeline', 'Touches release pipeline — COGS sub-bucket', '.github/workflows/release.yml'],
    ),
  }),
  commit({
    subject: 'chore: rotate npm publish token after audit log review',
    author: ENG_MATTCASEY,
    daysAgo: 11,
    additions: 1,
    deletions: 1,
    files_changed: 1,
    business_function_id: BF_RELEASE,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'cogs', source: 'system_auto' },
    suggestion: pin(
      clear('ktlo', 'opex'),
      ['rule.secret_rotation', 'Secret/credential rotation — KTLO security maintenance', '.github/secrets'],
    ),
  }),
  commit({
    subject: 'fix(spec): respect oneOf discriminator on missing-property edge',
    author: ENG_MEORPHIS,
    daysAgo: 7,
    additions: 28,
    deletions: 8,
    files_changed: 2,
    business_function_id: BF_SPEC_TOOLING,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(clear('ktlo', 'opex'), ['title.fix_prefix', 'Title begins with fix( — KTLO signal', '^fix\\(']),
  }),

  // ===== Enablement: CI, dev tooling, infra, internal =====
  commit({
    subject: 'feat(ci): cache turbo build artifacts — save 4 min per release',
    author: ENG_MATTCASEY,
    daysAgo: 28,
    additions: 47,
    deletions: 6,
    files_changed: 3,
    business_function_id: BF_RELEASE,
    decision: { investment_type: 'enablement', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('enablement', 'opex'),
      ['path.ci_workflow', 'Touches .github/workflows — internal CI change', '.github/workflows/release.yml'],
    ),
  }),
  commit({
    subject: 'feat(infra): GCE SDK builder async worker infra changes',
    author: ENG_MATTCASEY,
    daysAgo: 21,
    additions: 291,
    deletions: 38,
    files_changed: 7,
    business_function_id: BF_RELEASE,
    decision: { investment_type: 'enablement', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('enablement', 'opex'),
      ['path.infra', 'Async worker infra — internal scaling capacity', 'infra/builder/**'],
    ),
  }),
  commit({
    subject: 'feat(dev): run-acceptance-benchmark CLI for SDK releases',
    author: ENG_CJQUINES,
    daysAgo: 17,
    additions: 567,
    deletions: 14,
    files_changed: 9,
    business_function_id: BF_RELEASE,
    decision: { investment_type: 'enablement', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('enablement', 'opex'),
      ['path.bin', 'Internal CLI for release testing', 'bin/run-acceptance-benchmark'],
    ),
  }),
  commit({
    subject: 'feat(dev): playwright config for studio visual regression',
    author: ENG_CJQUINES,
    daysAgo: 13,
    additions: 89,
    deletions: 4,
    files_changed: 4,
    business_function_id: BF_STUDIO,
    decision: { investment_type: 'enablement', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('enablement', 'opex'),
      ['path.test_only', 'Touches playwright config + tests only', 'playwright.config.ts + tests/visual/**'],
    ),
  }),
  commit({
    subject: 'refactor(app): split API entirely from Apps',
    author: ENG_BKRAUSZ,
    daysAgo: 19,
    additions: 370,
    deletions: 412,
    files_changed: 28,
    business_function_id: BF_STUDIO,
    decision: { investment_type: 'enablement', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('enablement', 'opex'),
      ['title.refactor', 'Large-scale refactor — improves internal architecture', '^refactor'],
    ),
  }),
  // Gray-area: refactor "to support a new feature" — Growth or Enablement?
  commit({
    subject: 'refactor(spec): extract OpenAPI walker (prep for 3.1 ingestion)',
    author: ENG_MEORPHIS,
    daysAgo: 30,
    additions: 287,
    deletions: 234,
    files_changed: 9,
    business_function_id: BF_SPEC_TOOLING,
    decision: { investment_type: 'growth', accounting_treatment: 'opex', opex_subtype: 'rd', accounting_project_id: 'acctprj_openapi_31', source: 'system_auto' },
    suggestion: pin(
      gray('growth', 'enablement', 'opex'),
      ['title.refactor', 'Title begins with refactor — could be either Growth-prep or general cleanup', '^refactor\\('],
      ['title.linked_feature_mention', 'Title mentions an upcoming feature — Growth-prep signal', 'mentions "prep for 3.1 ingestion"'],
    ),
  }),
  commit({
    subject: 'chore(dev): docker-compose for local minio R2 emulation',
    author: ENG_SAMELBORAI,
    daysAgo: 8,
    additions: 38,
    deletions: 0,
    files_changed: 2,
    business_function_id: BF_RELEASE,
    decision: { investment_type: 'enablement', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('enablement', 'opex'),
      ['path.tooling', 'Local dev infra only', 'docker-compose.dev.yml'],
    ),
  }),
  // Observability ambiguous: customer-facing reliability or internal debug?
  commit({
    subject: 'feat(obs): add Datadog dashboard for codegen-worker latency',
    author: ENG_MATTCASEY,
    daysAgo: 6,
    additions: 0,
    deletions: 0,
    files_changed: 0,
    business_function_id: BF_RELEASE,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'cogs', source: 'system_auto' },
    suggestion: pin(
      gray('ktlo', 'enablement', 'opex'),
      ['classifier.observability_ambiguous', 'Observability config could be customer-facing reliability or internal dev', 'Defaults to KTLO+COGS; flag for review'],
    ),
  }),
  commit({
    subject: 'chore(dev): add commit-template + git hook for changelog tags',
    author: ENG_JACOBZ,
    daysAgo: 14,
    additions: 24,
    deletions: 0,
    files_changed: 2,
    business_function_id: BF_RELEASE,
    decision: { investment_type: 'enablement', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('enablement', 'opex'),
      ['path.tooling', 'Internal dev experience improvement', '.gitmessage + .githooks/'],
    ),
  }),

  // ===== Growth (no project) — net-new customer feature outside cap-approved scope =====
  commit({
    subject: 'feat(studio): publish-as-staged workflow for tentative SDK releases',
    author: ENG_YOUNGJIN,
    daysAgo: 25,
    additions: 312,
    deletions: 24,
    files_changed: 6,
    business_function_id: BF_STUDIO,
    decision: { investment_type: 'growth', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('growth', 'opex'),
      ['title.feat_prefix', 'Title begins with feat( — net-new functionality', '^feat\\('],
      ['classifier.no_cap_project', 'No linked cap-approved project — Growth flows to OpEx', '—'],
    ),
  }),
  commit({
    subject: 'feat(studio): per-org default branch + protected-branch enforcement',
    author: ENG_DACKERMAN,
    daysAgo: 17,
    additions: 234,
    deletions: 18,
    files_changed: 5,
    business_function_id: BF_STUDIO,
    decision: { investment_type: 'growth', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('growth', 'opex'),
      ['title.feat_prefix', 'Title begins with feat(', '^feat\\('],
      ['classifier.no_cap_project', 'No linked cap-approved project', '—'],
    ),
  }),
  commit({
    subject: 'feat(go): typed pagination for new cursor-style endpoints',
    author: ENG_DACKERMAN,
    daysAgo: 9,
    additions: 198,
    deletions: 12,
    files_changed: 4,
    business_function_id: BF_CODEGEN,
    decision: { investment_type: 'growth', accounting_treatment: 'opex', opex_subtype: 'cogs', source: 'system_auto' },
    suggestion: pin(
      clear('growth', 'opex'),
      ['title.feat_prefix', 'Title begins with feat(', '^feat\\('],
      ['path.go_template', 'Customer-specific template — COGS sub-bucket', 'templates/go/**'],
    ),
  }),

  // ===== Filler — long-tail and new hires =====
  commit({
    subject: 'fix(studio): clear button on OAS poll input',
    author: ENG_CJQUINES,
    daysAgo: 30,
    additions: 21,
    deletions: 7,
    files_changed: 1,
    business_function_id: BF_STUDIO,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(clear('ktlo', 'opex'), ['title.fix_prefix', 'Title begins with fix(', '^fix\\(']),
  }),
  commit({
    subject: 'chore: clean root package.json',
    author: ENG_CJQUINES,
    daysAgo: 27,
    additions: 8,
    deletions: 46,
    files_changed: 1,
    business_function_id: BF_RELEASE,
    decision: { investment_type: 'enablement', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(clear('enablement', 'opex'), ['path.lockfile_only', 'Only root manifest touched', 'package.json']),
  }),
  commit({
    subject: 'feat(ui-core): new EmptyState component for app + studio',
    author: ENG_ALEXARENA,
    daysAgo: 16,
    additions: 134,
    deletions: 0,
    files_changed: 3,
    business_function_id: BF_STUDIO,
    decision: { investment_type: 'enablement', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('enablement', 'opex'),
      ['path.ui_core', 'Internal shared component — Enablement', 'ui-core/EmptyState/**'],
    ),
  }),
  commit({
    subject: 'feat(studio): keyboard nav for diff hunks (j/k)',
    author: ENG_CHARLES,
    daysAgo: 5,
    additions: 89,
    deletions: 4,
    files_changed: 2,
    business_function_id: BF_STUDIO,
    decision: { investment_type: 'growth', accounting_treatment: 'capex', accounting_project_id: 'acctprj_studio_diff_v2', source: 'system_auto' },
    suggestion: pin(clear('growth', 'capex'), ['project.cap_approved', 'Linked project approved + application_dev', 'acctprj_studio_diff_v2']),
  }),
  commit({
    subject: 'fix(spec): Codegen — improve Schema_EnumDuplicateMembers diagnostic',
    author: ENG_ROBERT,
    daysAgo: 27,
    additions: 4,
    deletions: 2,
    files_changed: 1,
    business_function_id: BF_SPEC_TOOLING,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(clear('ktlo', 'opex'), ['title.fix_prefix', 'Title begins with fix(', '^fix\\(']),
  }),
  commit({
    subject: 'fix(codegen): remove unused ctx.customerConfig field',
    author: ENG_ROBERT,
    daysAgo: 16,
    additions: 1,
    deletions: 24,
    files_changed: 1,
    business_function_id: BF_CODEGEN,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(clear('ktlo', 'opex'), ['title.refactor', 'Dead code removal', 'codegen/ctx.ts']),
  }),
  commit({
    subject: 'refactor(java): goodbye nameSource — flat field names',
    author: ENG_TOMER,
    daysAgo: 14,
    additions: 92,
    deletions: 156,
    files_changed: 6,
    business_function_id: BF_CODEGEN,
    decision: { investment_type: 'enablement', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('enablement', 'opex'),
      ['title.refactor', 'Internal codegen restructure — Enablement', '^refactor\\('],
    ),
  }),

  // User-overridden CapEx → OpEx: human disagrees with system auto-classification.
  commit({
    subject: 'feat(studio): diff-row hover-highlight polish',
    author: ENG_YOUNGJIN,
    daysAgo: 4,
    additions: 38,
    deletions: 12,
    files_changed: 2,
    business_function_id: BF_STUDIO,
    decision: { investment_type: 'growth', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'user_pin', decided_by_user_id: ENG_MATTCASEY, rationale: 'Cosmetic polish — not directly attributable to the capitalized scope. OpEx.' },
    suggestion: pin(
      clear('growth', 'capex'),
      ['project.cap_approved', 'Linked project approved + application_dev', 'acctprj_studio_diff_v2'],
    ),
  }),
  // Gray-area: dep-bump-supporting-a-feature
  commit({
    subject: 'chore(deps): bump prosemirror to 1.5 — required for diff threads',
    author: ENG_YOUNGJIN,
    daysAgo: 13,
    additions: 8,
    deletions: 4,
    files_changed: 2,
    business_function_id: BF_STUDIO,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      gray('ktlo', 'growth', 'opex'),
      ['title.chore_deps', 'Title matches ^chore\\(deps\\)', '^chore\\(deps\\)'],
      ['title.linked_feature_mention', 'Title mentions a feature — directly-attributable cost candidate', 'mentions "diff threads"'],
      ['classifier.dep_for_feature', 'Spec default: KTLO+OpEx, flag for review', 'Δ confidence inv: 0.05'],
    ),
  }),
  // System fallback: classifier couldn't decide — defensive OpEx/R&D.
  commit({
    subject: 'wip: scratch — investigating producer-throughput regression',
    author: ENG_MEORPHIS,
    daysAgo: 3,
    additions: 14,
    deletions: 14,
    files_changed: 6,
    business_function_id: BF_RELEASE,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_fallback' },
    suggestion: pin(
      {
        investment_type: [
          ['ktlo', 0.34],
          ['enablement', 0.33],
          ['growth', 0.33],
        ],
        treatment: [
          ['opex', 0.52],
          ['capex', 0.48],
        ],
        rules: [],
      },
      ['classifier.no_signal', 'No rule fired — defensive fallback to OpEx/R&D + flag', 'subject: "wip: scratch"'],
    ),
  }),
  commit({
    subject: 'chore(deps): renovate weekly dep sweep',
    author: ENG_STAINLESS_BOT,
    daysAgo: 14,
    additions: 38,
    deletions: 38,
    files_changed: 4,
    business_function_id: BF_RELEASE,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('ktlo', 'opex'),
      ['title.chore_deps', 'Renovate dep sweep — predictable bot behavior', '^chore\\(deps\\)'],
      ['classifier.bot_shortcut', 'Bot-authored predictable PR — KTLO+OpEx by default', 'author: stainless-bot'],
    ),
  }),
  commit({
    subject: 'chore(deps): bump renovate to 38.x',
    author: ENG_STAINLESS_BOT,
    daysAgo: 9,
    additions: 2,
    deletions: 2,
    files_changed: 1,
    business_function_id: BF_RELEASE,
    decision: { investment_type: 'enablement', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('enablement', 'opex'),
      ['classifier.bot_shortcut', 'Bot-authored dev tooling bump', 'author: stainless-bot'],
    ),
  }),
  commit({
    subject: 'chore(deps): bump typescript to 5.7',
    author: ENG_STAINLESS_BOT,
    daysAgo: 4,
    additions: 4,
    deletions: 4,
    files_changed: 2,
    business_function_id: BF_RELEASE,
    decision: { investment_type: 'ktlo', accounting_treatment: 'opex', opex_subtype: 'rd', source: 'system_auto' },
    suggestion: pin(
      clear('ktlo', 'opex'),
      ['classifier.bot_shortcut', 'Bot-authored runtime dep bump', 'author: stainless-bot'],
    ),
  }),
];

// ---- Composer ----------------------------------------------------------

export function composeMockAccountingView(): AccountingView {
  // Compute per-engineer period cost and spread across that engineer's
  // commits in the window. Per spec §"Activity counting and Aggregation"
  // MVP: all commits weighted equally; per-commit cost =
  //   (annual_salary / 52 * period_weeks) / engineer_commit_count
  const commitsByAuthor = new Map<string, number>();
  for (const c of COMMITS) {
    commitsByAuthor.set(c.author_engineer_id, (commitsByAuthor.get(c.author_engineer_id) ?? 0) + 1);
  }
  const commitsWithCost: ClassifiedCommit[] = COMMITS.map((c) => {
    const salary = SALARY_USD[c.author_engineer_id] ?? 0;
    const tokenCost = AGENT_TOKEN_COST_USD[c.author_engineer_id] ?? 0;
    const authorCommitCount = commitsByAuthor.get(c.author_engineer_id) ?? 1;
    const periodSalaryCost = (salary / 52) * PERIOD_WEEKS;
    const perCommitLaborCost = periodSalaryCost / authorCommitCount;
    return { ...c, cost_usd: Math.round(perCommitLaborCost + tokenCost) };
  });

  return {
    object: 'accounting_view',
    branch_id: 'branch_mock',
    generated_at: NOW,
    period_start: PERIOD_START,
    period_end: PERIOD_END,
    poc_disclaimer:
      'POC: for demo purposes only. Capital allocation of last 30 days of commit history modelled on $200k unloaded salary for every engineer.',
    business_functions: [
      { id: BF_CODEGEN, display: 'SDK Codegen', subtitle: 'Multi-language SDK generation from OpenAPI specs' },
      { id: BF_STUDIO, display: 'Stainless Studio', subtitle: 'Config authoring, diff review, deployment' },
      { id: BF_API_DOCS, display: 'API Reference Docs', subtitle: 'Generated API reference docs shipped to customers' },
      { id: BF_SPEC_TOOLING, display: 'Spec & Config Tooling', subtitle: 'OpenAPI ingestion + .stainless.yml' },
      { id: BF_RELEASE, display: 'Release Automation', subtitle: 'Bots, CI, and release pipeline' },
    ],
    engineers: [
      { id: ENG_ROBERT, display_name: 'Robert Craigie', kind: 'human', operator_engineer_id: null },
      { id: ENG_YOUNGJIN, display_name: 'Young-Jin Park', kind: 'human', operator_engineer_id: null },
      { id: ENG_MEORPHIS, display_name: 'Eric Morphis', kind: 'human', operator_engineer_id: null },
      { id: ENG_TOMER, display_name: 'Tomer Aberbach', kind: 'human', operator_engineer_id: null },
      { id: ENG_MATTCASEY, display_name: 'Matt Casey', kind: 'human', operator_engineer_id: null },
      { id: ENG_CJQUINES, display_name: 'CJ Quines', kind: 'human', operator_engineer_id: null },
      { id: ENG_RYANPAUL, display_name: 'Ryan Paul', kind: 'human', operator_engineer_id: null },
      { id: ENG_SAMELBORAI, display_name: 'Sam El-Borai', kind: 'human', operator_engineer_id: null },
      { id: ENG_DACKERMAN, display_name: 'David Ackerman', kind: 'human', operator_engineer_id: null },
      { id: ENG_BKRAUSZ, display_name: 'Brian Krausz', kind: 'human', operator_engineer_id: null },
      { id: ENG_EM, display_name: 'Em', kind: 'human', operator_engineer_id: null },
      { id: ENG_ALEXARENA, display_name: 'Alex Arena', kind: 'human', operator_engineer_id: null },
      { id: ENG_JACOBZ, display_name: 'Jacob Zimmerman', kind: 'human', operator_engineer_id: null },
      { id: ENG_CHARLES, display_name: 'Charles Finkel', kind: 'human', operator_engineer_id: null },
      { id: ENG_RATTRAY, display_name: 'Alex Rattray', kind: 'human', operator_engineer_id: null },
      { id: ENG_STAINLESS_BOT, display_name: 'stainless-bot', kind: 'agent', operator_engineer_id: ENG_MATTCASEY },
    ],
    accounting_projects: PROJECTS,
    commits: commitsWithCost,
  };
}
